var _excluded = ["name", "type", "placeholder", "loading", "errorMessage", "description", "suffix", "label", "value", "disabled", "required", "isValid", "isEmpty", "onChange", "onFocus", "onBlur", "onClear", "inputMode", "inputRef", "mode"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import * as React from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import { objectFilter } from 'fast-loops';
import Box from '../box';
import Text from '../text';
import IconButton from '../iconButton';
import { IconSearch, IconClose } from '../icons';
import BaseInput from './BaseInput.js';
var baseDisplayStyle = {
  display: 'none'
};
var hideClearButtonStyle = {
  '::-webkit-search-decoration': baseDisplayStyle,
  '::-webkit-search-cancel-button': baseDisplayStyle,
  '::-webkit-search-results-button': baseDisplayStyle,
  '::-webkit-search-results-decoration': baseDisplayStyle
};
function getIcon(type) {
  if (type === 'search') {
    return IconSearch;
  }
  return null;
}
function Suffix(_ref) {
  var children = _ref.children;
  var _useFela = useFela(),
    theme = _useFela.theme;
  return /*#__PURE__*/React.createElement(Box, {
    bg: "eucalyptus100",
    alignitems: "center",
    justifyContent: "center",
    paddingHorizontal: theme.tokens.inputPaddingHorizontal / theme.baselineGrid,
    extend: {
      borderLeftWidth: 1,
      borderLeftStyle: 'solid',
      borderLeftColor: 'border',
      borderTopRightRadius: theme.tokens.cornersSmall,
      borderBottomRightRadius: theme.tokens.cornersSmall
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: "eucalyptus700"
  }, children));
}
export default function TextInput(_ref2) {
  var name = _ref2.name,
    _ref2$type = _ref2.type,
    type = _ref2$type === void 0 ? 'text' : _ref2$type,
    placeholder = _ref2.placeholder,
    loading = _ref2.loading,
    errorMessage = _ref2.errorMessage,
    description = _ref2.description,
    suffix = _ref2.suffix,
    _ref2$label = _ref2.label,
    label = _ref2$label === void 0 ? '' : _ref2$label,
    _ref2$value = _ref2.value,
    value = _ref2$value === void 0 ? '' : _ref2$value,
    _ref2$disabled = _ref2.disabled,
    disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
    _ref2$required = _ref2.required,
    required = _ref2$required === void 0 ? false : _ref2$required,
    _ref2$isValid = _ref2.isValid,
    isValid = _ref2$isValid === void 0 ? true : _ref2$isValid,
    isEmpty = _ref2.isEmpty,
    onChange = _ref2.onChange,
    onFocus = _ref2.onFocus,
    onBlur = _ref2.onBlur,
    onClear = _ref2.onClear,
    inputMode = _ref2.inputMode,
    inputRef = _ref2.inputRef,
    mode = _ref2.mode,
    props = _objectWithoutProperties(_ref2, _excluded);
  var inputProps = objectFilter(props, function (_, key) {
    return key !== 'data-test';
  });
  var Icon = getIcon(type);
  var shouldShowClearButton = onClear && !loading && !disabled && value.length > 0;
  return /*#__PURE__*/React.createElement(BaseInput, {
    name: name,
    label: label,
    value: value,
    isValid: isValid,
    isEmpty: isEmpty,
    disabled: disabled,
    loading: loading,
    type: type,
    mode: mode,
    errorMessage: errorMessage,
    description: description,
    "data-test": props['data-test'],
    inputStyle: {
      extend: [{
        condition: type === 'search',
        style: hideClearButtonStyle
      }, {
        condition: type === 'number',
        style: {
          '::-webkit-inner-spin-button': {
            appearance: 'none',
            margin: 0
          }
        }
      }]
    },
    before: Icon && /*#__PURE__*/React.createElement(Box, {
      extend: {
        pointerEvents: 'none',
        position: 'relative'
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      color: "foreground.tertiary"
    })),
    after: /*#__PURE__*/React.createElement(React.Fragment, null, shouldShowClearButton && /*#__PURE__*/React.createElement(Box, {
      alignSelf: "center",
      marginRight: [-1,,, 1],
      marginLeft: -4,
      extend: {
        position: 'relative'
      }
    }, /*#__PURE__*/React.createElement(IconButton, {
      label: "Clear value",
      iconSize: 12,
      icon: IconClose,
      onClick: onClear
    })), suffix && /*#__PURE__*/React.createElement(Suffix, null, suffix))
  }, function (innerProps) {
    return /*#__PURE__*/React.createElement("input", _extends({}, inputProps, innerProps, {
      ref: inputRef,
      type: type,
      inputMode: inputMode,
      required: required,
      placeholder: placeholder,
      onChange: onChange,
      onFocus: onFocus,
      onBlur: onBlur,
      onWheel: function onWheel(e) {
        return e.target.blur();
      }
    }));
  });
}
TextInput.propTypes = {
  /** The change event handler. */
  onChange: PropTypes.func.isRequired,
  /** The bur event handler. */
  onBlur: PropTypes.func,
  /** The focus event handler. */
  onFocus: PropTypes.func,
  /** A function that is called when clicking the clear button that appears upon passing a value */
  onClear: PropTypes.func,
  /** A unique semantic name that is connected to the label. */
  name: PropTypes.string.isRequired,
  /** The controlled value. */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Sets the validation state. */
  isValid: PropTypes.bool,
  /** A function that takes the value and returns a boolean that indicates an empty state */
  isEmpty: PropTypes.func,
  /** Sets disabled. */
  disabled: PropTypes.bool,
  /** Sets required. */
  required: PropTypes.bool,
  /** Whether the input is loading */
  loading: PropTypes.bool,
  /** The label text. */
  label: PropTypes.string,
  /** An error message text that is displayed once isValid is false. */
  errorMessage: PropTypes.string,
  /** Additional description information display beneath the input. */
  description: PropTypes.string,
  /** The input placeholder */
  placeholder: PropTypes.string,
  /** The input type. */
  type: PropTypes.oneOf(['text', 'number', 'tel', 'search', 'email', 'password', 'date', 'datetime-local', 'week']),
  mode: PropTypes.oneOf(['compact']),
  /** The inputmode global attribute is an enumerated attribute that hints at the type of data that might be entered by the user while editing the element or its contents. This allows a browser to display an appropriate virtual keyboard. */
  inputMode: PropTypes.oneOf(['text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url']),
  /** Text suffix at the end of the input  */
  suffix: PropTypes.string,
  /** The input will have focus when page has finished loading. Use with caution because autoFocus scrolls to the element if not in sight. It can confuse screen readers  */
  autoFocus: PropTypes.bool,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })])
};